import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  type UrlTree,
} from '@angular/router';
import { WorkspaceService } from '@principle-theorem/ng-auth';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { Brand, Organisation } from '@principle-theorem/principle-core';
import {
  Firestore,
  firstValueFrom,
  ORG_ID_PARAM,
  where,
} from '@principle-theorem/shared';
import { filter, timeout } from 'rxjs/operators';

/**
 * Route guard that handles the `?orgId=<id>` query parameter for deep-linking
 * into a specific workspace/organisation.
 *
 * When any URL includes the `orgId` query parameter, this guard:
 * 1. Looks up the matching workspace from the user's available workspaces
 * 2. Sets it as the active workspace (persisted to IndexedDB and sessionStorage)
 * 3. Resolves the brand and practice from the URL path slugs
 * 4. Strips the `orgId` param and triggers a full page reload so all
 *    observables re-initialise from the newly persisted workspace
 *
 * If the user has no access to the requested organisation, they are redirected
 * to `/workspaces` with a `redirectTo` param preserving the original URL.
 *
 * If no `orgId` param is present, the guard passes through (`true`).
 */
@Injectable({
  providedIn: 'root',
})
export class OrgIdGuard {
  constructor(
    private _router: Router,
    private _workspace: WorkspaceService,
    private _organisation: OrganisationService
  ) {}

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const urlTree = this._router.parseUrl(state.url);
    const orgId = urlTree.queryParamMap.get(ORG_ID_PARAM) ?? undefined;

    if (!orgId) {
      return true;
    }

    try {
      // Wait for workspaces to be populated by AuthService.checkWorkspaceAccess().
      // workspaces$ is a ReplaySubject(1) that won't emit until checkWorkspaceAccess
      // completes — which runs asynchronously after auth. Using snapshot() here would
      // hang forever if workspaces haven't loaded yet.
      const workspaces = await firstValueFrom(
        this._workspace.workspaces$.pipe(
          filter((ws) => ws.length > 0),
          timeout(10_000)
        )
      ).catch(() => [] as never[]);

      const workspace = workspaces?.find(({ uid }) => uid === orgId);

      if (!workspace) {
        return this._router.createUrlTree(['/workspaces'], {
          queryParams: { redirectTo: state.url },
        });
      }

      await this._workspace.setWorkspace(workspace);

      const org = await Firestore.getDoc(
        Firestore.doc(Organisation.col(), orgId)
      );
      if (!org) {
        return this._redirectToCleanUrl(state);
      }

      const segments = urlTree.root.children['primary']?.segments ?? [];
      const brandSlug = segments[0]?.path;
      const practiceSlug = segments[1]?.path;

      if (brandSlug) {
        const brand = await Firestore.firstResult(
          Organisation.brandCol(org),
          where('slug', '==', brandSlug)
        );

        if (brand) {
          this._organisation.selectBrand(brand);

          if (practiceSlug) {
            const practiceDoc = await Firestore.firstResult(
              Brand.practiceCol(brand),
              where('slug', '==', practiceSlug)
            );

            if (practiceDoc) {
              this._organisation.selectPractice(practiceDoc);
            }
          }
        }
      }

      const cleanUrl = this._cleanUrl(state);
      window.location.href = cleanUrl;

      // Block Angular navigation until the browser reload kicks in
      return await new Promise<boolean>(() => {
        //
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[OrgIdGuard] error', { orgId, error });
      return this._router.createUrlTree(['/workspaces'], {
        queryParams: { redirectTo: state.url },
      });
    }
  }

  private _redirectToCleanUrl(state: RouterStateSnapshot): UrlTree {
    return this._router.parseUrl(this._cleanUrl(state));
  }

  private _cleanUrl(state: RouterStateSnapshot): string {
    const urlTree = this._router.parseUrl(state.url);
    delete urlTree.queryParams[ORG_ID_PARAM];
    return urlTree.toString();
  }
}
